import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { BookOpen, Bookmark, Clock, Plus, ChevronRight, FileText, Globe, MapPin, Trash2, X, Link2, Sparkles, Image as ImageIcon } from 'lucide-react'
import { useWireAuth } from '../auth/useWireAuth'
import { usePages, useDeletePage, useSavedArticlesPreview, useRecentlyRead, useToggleSavedArticle, useRecommendedArticles } from '../api/hooks'
import type { Page, PageKind, WireArticle } from '../api/types'
import { MyWireArticleRow } from '../components/mywire/MyWireArticleRow'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function greeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

const KIND_ICON: Record<PageKind, React.ElementType> = {
  'front-page': Globe,
  topic: FileText,
  region: MapPin,
  edition: BookOpen,
  book: BookOpen,
}


// ─── Book Card ────────────────────────────────────────────────────────────────

function BookCard({ book }: { book: Page }) {
  const Icon = KIND_ICON[book.kind]
  const [confirming, setConfirming] = useState(false)
  const [copied, setCopied] = useState(false)
  const { mutate: deletePage, isPending: isDeleting } = useDeletePage()

  function handleShare(e: React.MouseEvent) {
    e.preventDefault()
    const url = `${window.location.origin}/wire/books/${book.id}?nav_method=gen_link`
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleDelete(e: React.MouseEvent) {
    e.preventDefault()
    deletePage(book.id, {
      onError: () => toast.error('Failed to delete book.'),
    })
  }

  return (
    <div className="group relative flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-fill">
      <Link to={`/wire/books/${book.id}`} className="absolute inset-0 rounded-md" aria-label={book.title} />
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface text-fg-secondary">
        <Icon size={14} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-fg">{book.title}</p>
        <p className="mt-0.5 text-[11px] text-fg-secondary">
          Updated {formatDate(book.updatedAt)}
        </p>
      </div>
      {confirming ? (
        <div className="relative z-10 flex items-center gap-1.5">
          <span className="text-[10px] font-semibold text-error-fg">Delete?</span>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="rounded border border-error-line bg-error-surface px-2 py-0.5 text-[10px] font-semibold text-error-fg transition-colors hover:bg-error-fill disabled:opacity-50"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill"
          >
            No
          </button>
        </div>
      ) : (
        <div className="relative z-10 flex items-center">
          <button
            type="button"
            onClick={handleShare}
            aria-label="Share book"
            className="rounded p-1 text-fg-secondary opacity-60 transition-colors hover:bg-fill hover:text-fg"
          >
            <Link2 size={12} aria-hidden="true" className={copied ? 'text-teal-9' : ''} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setConfirming(true) }}
            aria-label="Delete book"
            className="rounded p-1 text-fg-secondary opacity-60 transition-colors hover:bg-fill hover:text-error-fg"
          >
            <Trash2 size={12} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Mini-table loading / empty states ────────────────────────────────────────

function RowSkeletons({ count }: { count: number }) {
  return (
    <div className="space-y-2">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="h-[64px] animate-pulse rounded-md bg-fill" />
      ))}
    </div>
  )
}

function EmptyPanel({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex min-h-[120px] flex-col items-center justify-center gap-1 rounded-md bg-fill/60 text-center">
      <p className="text-xs font-semibold text-fg-secondary opacity-60">{title}</p>
      <p className="text-[11px] text-fg-secondary opacity-40">{subtitle}</p>
    </div>
  )
}

function RecommendedArticleRow({ article }: { article: WireArticle }) {
  const id = article.articleId ?? article.id
  const thumbUrl = article.thumbnail?.presignedUrl
  const byline = article.byline || (article.authors?.length ? `By ${article.authors.join(', ')}` : null)
  const meta = [byline, formatDate(article.publishedAt ?? article.updatedAt)].filter(Boolean).join(' · ')

  return (
    <Link
      to={`/wire/articles/${id}`}
      state={{ navMethod: 'recommended' as const }}
      className="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-fill"
    >
      {thumbUrl ? (
        <img src={thumbUrl} alt="" className="h-14 w-14 shrink-0 rounded object-cover" />
      ) : (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-fill">
          <ImageIcon size={16} className="text-fg-secondary opacity-30" aria-hidden="true" />
        </div>
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-fg">{article.title}</p>
        {meta && <p className="mt-1 truncate text-[11px] text-fg-secondary">{meta}</p>}
      </div>
    </Link>
  )
}

// ─── MyWirePage ───────────────────────────────────────────────────────────────

export default function MyWirePage() {
  const { user } = useWireAuth()
  const displayName = user?.given_name ?? user?.name ?? 'there'

  // Fetch only this user's books
  const { data: books, isLoading: booksLoading } = usePages({ kind: 'book', createdBy: 'me' })

  const myBooks = books?.filter((p) => p.status !== 'archived' && p.createdBy === user?.sub) ?? []

  const { data: savedPage, isLoading: savedLoading } = useSavedArticlesPreview(3)
  const savedArticles = savedPage?.items ?? []
  const toggleSaved = useToggleSavedArticle()
  const [removedArticles, setRemovedArticles] = useState<WireArticle[]>([])

  function handleRemove(article: WireArticle) {
    setRemovedArticles((prev) => [...prev, article])
    toggleSaved.mutate({ article, nextSaved: false })
  }

  function handleUndo(article: WireArticle) {
    setRemovedArticles((prev) => prev.filter((a) => a.id !== article.id))
    toggleSaved.mutate({ article, nextSaved: true })
  }

  function handleDismiss() {
    setRemovedArticles([])
  }

  const { data: recentPage, isLoading: recentLoading } = useRecentlyRead(3)
  const recentArticles = recentPage?.items ?? []
  const { data: recommendedPage, isLoading: recommendedLoading } = useRecommendedArticles()
  const recommendedArticles = (recommendedPage ?? []).slice(0, 5)

  return (
    <div className="relative">

      {/* Hero wash */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72"
        style={{
          background: 'linear-gradient(to bottom, var(--violet-4), var(--violet-2), transparent)',
          opacity: 0.45,
        }}
        aria-hidden="true"
      />

      <div className="relative w-full px-6 py-12 sm:px-8 lg:px-12">

        {/* Greeting header */}
        <div className="mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-fg-secondary">
            My Wire
          </p>
          <h1 className="font-zodiak mt-2 text-4xl font-bold text-fg">
            {greeting()}, {displayName}.
          </h1>
          <p className="mt-2 text-sm text-fg-secondary">
            Your personal reading space.
          </p>
        </div>

        {/* Sections */}
        <div className="grid gap-6 lg:grid-cols-2">

          {/* My Books */}
          <section className="min-w-0 border-t border-line pt-4">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen size={16} aria-hidden="true" className="text-fg-secondary" />
                <h2 className="text-lg font-semibold text-fg">My Books</h2>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/wire/me/books/new"
                  className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-[11px] font-semibold text-fg transition-colors hover:bg-fill"
                >
                  <Plus size={11} aria-hidden="true" />
                  New Book
                </Link>
                <Link
                  to="/wire/me/books"
                  className="flex items-center gap-0.5 text-[11px] font-semibold text-fg-secondary transition-colors hover:text-fg"
                >
                  See all
                  <ChevronRight size={11} aria-hidden="true" />
                </Link>
              </div>
            </div>

            {booksLoading ? (
              <div className="space-y-1">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-[52px] animate-pulse rounded-md bg-fill" />
                ))}
              </div>
            ) : myBooks.length === 0 ? (
              <div className="flex min-h-[140px] flex-col items-center justify-center gap-3 rounded-md bg-fill/60">
                <BookOpen size={20} className="text-fg-secondary opacity-30" aria-hidden="true" />
                <div className="text-center">
                  <p className="text-xs font-semibold text-fg-secondary opacity-60">No books yet.</p>
                  <p className="mt-0.5 text-[11px] text-fg-secondary opacity-40">
                    Create a book to organize and share articles.
                  </p>
                </div>
                <Link
                  to="/wire/me/books/new"
                  className="mt-1 flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-[11px] font-semibold text-fg transition-colors hover:bg-fill"
                >
                  <Plus size={11} aria-hidden="true" />
                  Create your first book
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-line">
                {myBooks.slice(0, 8).map((book) => (
                  <BookCard key={book.id} book={book} />
                ))}
              </div>
            )}
          </section>

          {/* Saved Articles */}
          <section className="min-w-0 border-t border-line pt-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Bookmark size={16} aria-hidden="true" className="text-fg-secondary" />
                  <h2 className="text-lg font-semibold text-fg">Saved Articles</h2>
                </div>
                <Link
                  to="/wire/me/saved"
                  className="flex items-center gap-0.5 text-[11px] font-semibold text-fg-secondary transition-colors hover:text-fg"
                >
                  See all
                  <ChevronRight size={11} aria-hidden="true" />
                </Link>
              </div>

              {removedArticles.length > 0 && (
                <div className="mb-3 flex items-start justify-between gap-2 rounded-md bg-fill px-3 py-2.5">
                  <div className="space-y-1">
                    {removedArticles.map((article) => (
                      <p key={article.id} className="text-xs text-fg-secondary">
                        <span className="font-medium text-fg">{article.title}</span> was removed.{' '}
                        <button
                          type="button"
                          onClick={() => handleUndo(article)}
                          className="font-semibold text-accent-primary-fg underline hover:no-underline"
                        >
                          Undo
                        </button>
                      </p>
                    ))}
                  </div>
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={handleDismiss}
                    className="shrink-0 rounded p-0.5 text-fg-secondary transition-colors hover:text-fg"
                  >
                    <X size={13} aria-hidden="true" />
                  </button>
                </div>
              )}

              {savedLoading ? (
                <RowSkeletons count={3} />
              ) : savedArticles.length === 0 && removedArticles.length === 0 ? (
                <EmptyPanel
                  title="No saved articles yet."
                  subtitle="Articles you bookmark will appear here."
                />
              ) : savedArticles.length > 0 ? (
                <div className="space-y-1">
                  {savedArticles.map((article) => (
                    <MyWireArticleRow key={article.id} article={article} onRemove={handleRemove} />
                  ))}
                </div>
              ) : null}
          </section>

          {/* Recommended */}
          <section className="min-w-0 border-t border-line pt-4">
              <div className="mb-4 flex items-center gap-2">
                <Sparkles size={16} aria-hidden="true" className="text-fg-secondary" />
                <h2 className="text-lg font-semibold text-fg">Recommended For You</h2>
              </div>

              {recommendedLoading ? (
                <RowSkeletons count={3} />
              ) : recommendedArticles.length === 0 ? (
                <EmptyPanel
                  title="No recommendations yet."
                  subtitle="Read more articles to personalize this feed."
                />
              ) : (
                <div className="space-y-1">
                  {recommendedArticles.map((article) => (
                    <RecommendedArticleRow key={article.id} article={article} />
                  ))}
                </div>
              )}
          </section>

          {/* Recently Read */}
          <section className="min-w-0 border-t border-line pt-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Clock size={16} aria-hidden="true" className="text-fg-secondary" />
                  <h2 className="text-lg font-semibold text-fg">Recently Read</h2>
                </div>
                <Link
                  to="/wire/me/history"
                  className="flex items-center gap-0.5 text-[11px] font-semibold text-fg-secondary transition-colors hover:text-fg"
                >
                  See all
                  <ChevronRight size={11} aria-hidden="true" />
                </Link>
              </div>

              {recentLoading ? (
                <RowSkeletons count={3} />
              ) : recentArticles.length === 0 ? (
                <EmptyPanel
                  title="Nothing here yet."
                  subtitle="Articles you read will show up here."
                />
              ) : (
                <div className="space-y-1">
                  {recentArticles.map((article) => (
                    <MyWireArticleRow key={article.id} article={article} />
                  ))}
                </div>
              )}
          </section>

        </div>
      </div>
    </div>
  )
}
